import type { Vector4 } from "../../types/scene";

export interface MjcfOrientationSettings {
  eulerseq: string;
  angleUnit: "radian" | "degree";
}

const quatMul = (a: Vector4, b: Vector4): Vector4 => {
  const [aw, ax, ay, az] = a;
  const [bw, bx, by, bz] = b;
  return [
    aw * bw - ax * bx - ay * by - az * bz,
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
  ];
};

const eulerToQuat = (angles: number[], settings: MjcfOrientationSettings): Vector4 => {
  const toRad = settings.angleUnit === "degree" ? Math.PI / 180 : 1;
  const a = (angles[0] || 0) * toRad;
  const b = (angles[1] || 0) * toRad;
  const c = (angles[2] || 0) * toRad;
  const seq = (settings.eulerseq || "xyz").toLowerCase();
  const makeQuat = (axis: string, angle: number): Vector4 => {
    const s = Math.sin(angle / 2);
    const w = Math.cos(angle / 2);
    if (axis === "x") return [w, s, 0, 0];
    if (axis === "y") return [w, 0, s, 0];
    return [w, 0, 0, s];
  };
  const q0 = makeQuat(seq[0] ?? "x", a);
  const q1 = makeQuat(seq[1] ?? "y", b);
  const q2 = makeQuat(seq[2] ?? "z", c);
  return quatMul(quatMul(q0, q1), q2);
};

const axisAngleToQuat = (axisAngle: number[], settings: MjcfOrientationSettings): Vector4 => {
  const ax = axisAngle[0] || 0;
  const ay = axisAngle[1] || 0;
  const az = axisAngle[2] || 0;
  const rawAngle = axisAngle[3] || 0;
  const angleRad = settings.angleUnit === "degree" ? (rawAngle * Math.PI) / 180 : rawAngle;
  const axisMag = Math.hypot(ax, ay, az);
  if (axisMag < 1e-8) return [1, 0, 0, 0];
  const nx = ax / axisMag;
  const ny = ay / axisMag;
  const nz = az / axisMag;
  const s = Math.sin(angleRad / 2);
  return [Math.cos(angleRad / 2), nx * s, ny * s, nz * s];
};

const normalizeVec3 = (v: number[]): [number, number, number] | null => {
  if (!Array.isArray(v) || v.length < 3) return null;
  const x = Number(v[0]) || 0;
  const y = Number(v[1]) || 0;
  const z = Number(v[2]) || 0;
  const mag = Math.hypot(x, y, z);
  if (mag < 1e-8) return null;
  return [x / mag, y / mag, z / mag];
};

const cross = (
  a: [number, number, number],
  b: [number, number, number]
): [number, number, number] => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

const quatFromBasis = (
  xAxis: [number, number, number],
  yAxis: [number, number, number],
  zAxis: [number, number, number]
): Vector4 => {
  const [m00, m10, m20] = xAxis;
  const [m01, m11, m21] = yAxis;
  const [m02, m12, m22] = zAxis;
  const trace = m00 + m11 + m22;
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2;
    return [0.25 * s, (m21 - m12) / s, (m02 - m20) / s, (m10 - m01) / s];
  }
  if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1 + m00 - m11 - m22) * 2;
    return [(m21 - m12) / s, 0.25 * s, (m01 + m10) / s, (m02 + m20) / s];
  }
  if (m11 > m22) {
    const s = Math.sqrt(1 + m11 - m00 - m22) * 2;
    return [(m02 - m20) / s, (m01 + m10) / s, 0.25 * s, (m12 + m21) / s];
  }
  const s = Math.sqrt(1 + m22 - m00 - m11) * 2;
  return [(m10 - m01) / s, (m02 + m20) / s, (m12 + m21) / s, 0.25 * s];
};

const xyaxesToQuat = (xyaxes: number[]): Vector4 | null => {
  if (!Array.isArray(xyaxes) || xyaxes.length < 6) return null;
  const x = normalizeVec3([xyaxes[0], xyaxes[1], xyaxes[2]]);
  const yRaw = normalizeVec3([xyaxes[3], xyaxes[4], xyaxes[5]]);
  if (!x || !yRaw) return null;
  const z = normalizeVec3(cross(x, yRaw));
  if (!z) return null;
  const y = normalizeVec3(cross(z, x));
  if (!y) return null;
  return quatFromBasis(x, y, z);
};

const zaxisToQuat = (zaxis: number[]): Vector4 | null => {
  const z = normalizeVec3(zaxis);
  if (!z) return null;
  const worldUp: [number, number, number] = Math.abs(z[2]) > 0.999 ? [1, 0, 0] : [0, 0, 1];
  const x = normalizeVec3(cross(worldUp, z));
  if (!x) return null;
  const y = normalizeVec3(cross(z, x));
  if (!y) return null;
  return quatFromBasis(x, y, z);
};

export const parseElementOrientation = (
  elem: Element,
  settings: MjcfOrientationSettings
): Vector4 => {
  const quatAttr = elem.getAttribute("quat");
  if (quatAttr) {
    const v = quatAttr.trim().split(/\s+/).map(Number);
    if (v.length === 4 && v.every(Number.isFinite)) return v as Vector4;
  }
  const eulerAttr = elem.getAttribute("euler");
  if (eulerAttr) {
    const v = eulerAttr.trim().split(/\s+/).map(Number);
    if (v.length === 3 && v.every(Number.isFinite)) return eulerToQuat(v, settings);
  }
  const axisangleAttr = elem.getAttribute("axisangle");
  if (axisangleAttr) {
    const v = axisangleAttr.trim().split(/\s+/).map(Number);
    if (v.length >= 4 && v.every(Number.isFinite)) return axisAngleToQuat(v, settings);
  }
  const xyaxesAttr = elem.getAttribute("xyaxes");
  if (xyaxesAttr) {
    const v = xyaxesAttr.trim().split(/\s+/).map(Number);
    return xyaxesToQuat(v) ?? [1, 0, 0, 0];
  }
  const zaxisAttr = elem.getAttribute("zaxis");
  if (zaxisAttr) {
    const v = zaxisAttr.trim().split(/\s+/).map(Number);
    return zaxisToQuat(v) ?? [1, 0, 0, 0];
  }
  return [1, 0, 0, 0];
};

export const normalizeQuat = (q: Vector4): Vector4 => {
  const [w, x, y, z] = q;
  const mag = Math.sqrt(w * w + x * x + y * y + z * z);
  return mag > 0 ? [w / mag, x / mag, y / mag, z / mag] : [1, 0, 0, 0];
};
